import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Minus, Sparkles, ArrowLeft, Shield, Flame, ShieldCheck, Loader2, Users, Lightbulb, AlertTriangle, Brain, TrendingUp, MessageSquareHeart, ShieldAlert, Heart, BookOpen, PartyPopper, UserPlus, Clock } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { PRICING, IS_PRELAUNCH } from "@/lib/pricing";
import { Events } from "@/lib/analytics";
import { initBilling, purchase as iapPurchase, restorePurchases } from "@/lib/billing";
import ReserveDealModal from "@/components/ReserveDealModal";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Comparison matrix — only real, built features.
// `free` = what Free-tier users actually get today.
// `pro` = what PRO unlocks on top.
const COMPARISON = [
  { label: "Daily message checks",          free: "3 / day",     pro: "Unlimited" },
  { label: "Risk-meter analysis",           free: true,          pro: true },
  { label: "Relationship type",             free: "General",     pro: "All 5" },
  { label: "Tone options",                  free: "2 tones",     pro: "All 6 tones" },
  { label: "Rewrite suggestions",           free: "1 per message", pro: "3 per message" },
  { label: "Received-message context",      free: false,         pro: true },
  { label: "Conflict Score tracking",       free: "Preview",     pro: "Full" },
  { label: "Saved message history",         free: "30 days",     pro: "Forever" },
  { label: "Early access to new features",  free: false,         pro: true },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reserveOpen, setReserveOpen] = useState(false);
  const [purchasing, setPurchasing] = useState(null);
  const [waitlistCount, setWaitlistCount] = useState(null);

  useEffect(() => {
    Events.viewPricing("pricing_page");
    // Initialize billing when user visits pricing (safe no-op on web / prelaunch)
    if (!IS_PRELAUNCH) {
      initBilling(user?.id);
    }
    // Load waitlist count for social proof
    if (IS_PRELAUNCH) {
      axios
        .get(`${BACKEND_URL}/api/early-access/count`)
        .then((res) => setWaitlistCount(res.data?.count ?? null))
        .catch(() => setWaitlistCount(null));
    }
  }, [user?.id]);

  const handlePurchase = async (plan) => {
    setPurchasing(plan);
    const res = await iapPurchase(plan);
    setPurchasing(null);
    if (res.ok) {
      toast.success("Welcome to PRO!");
      // TODO: refresh user PRO status on backend
      navigate("/app", { replace: true });
    } else if (res.error && res.error !== "Cancelled") {
      toast.error(res.error);
    }
  };

  const handleYearly = () => {
    Events.upgradeClick("yearly_5_99", "pricing_page");
    if (IS_PRELAUNCH) {
      setReserveOpen(true);
      return;
    }
    handlePurchase("yearly");
  };

  const handleMonthly = () => {
    Events.upgradeClick("monthly_2_99", "pricing_page");
    if (IS_PRELAUNCH) {
      setReserveOpen(true);
      return;
    }
    handlePurchase("monthly");
  };

  const handleRestore = async () => {
    const res = await restorePurchases();
    if (res.ok && res.active) {
      toast.success("PRO restored!");
      navigate("/app", { replace: true });
    } else if (res.ok) {
      toast.info("No active subscription found on this account.");
    } else {
      toast.error(res.error || "Restore failed.");
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FB] pb-16" data-testid="pricing-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm border-b border-[#E5E7EB]">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            data-testid="pricing-back-btn"
            className="gap-1.5 text-[#6B7280] hover:text-[#1F3A5F] hover:bg-[#EAF6F4] rounded-xl"
          >
            <ArrowLeft size={18} />
            Back
          </Button>
          <Logo size="small" />
          <div className="w-16" />
        </div>
      </div>

      <div className="max-w-xl mx-auto px-5 pt-8">
        {/* Pre-launch reassurance bar */}
        {IS_PRELAUNCH && (
          <div
            className="flex items-start gap-3 p-3 mb-5 rounded-2xl border-2 border-[#FEB95A]/40"
            style={{ background: "linear-gradient(135deg, #FFF8EB 0%, #FFE7B8 100%)" }}
            data-testid="prelaunch-banner"
          >
            <ShieldCheck size={20} className="text-[#8B4A00] flex-shrink-0 mt-0.5" />
            <div className="text-[#1F3A5F] text-[13px] leading-snug">
              <strong>Pre-launch — no charge today.</strong> Reserve your spot and we'll email a promo code
              when SayItBetter goes live on Google Play or the App Store.
            </div>
          </div>
        )}

        {/* Hero */}
        <div className="text-center mb-8 animate-fadeIn">
          <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-[#FEE4A4] to-[#FFD27F] text-[#8B4A00] px-3 py-1 rounded-full text-xs font-bold mb-4 shadow-sm">
            <Flame size={13} />
            {PRICING.promo.badge.toUpperCase()}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1F3A5F] mb-3 leading-tight tracking-tight" data-testid="pricing-headline">
            Communicate Like Your Best Self
          </h1>
          <p className="text-[#6B7280] text-base leading-relaxed" data-testid="pricing-subheadline">
            Unlock the full power of SayItBetter
          </p>
          {IS_PRELAUNCH && waitlistCount !== null && waitlistCount > 0 && (
            <div
              className="inline-flex items-center gap-1.5 mt-4 bg-[#EAF6F4] border border-[#4FB3A5]/30 text-[#146B5F] text-xs font-semibold px-3 py-1.5 rounded-full"
              data-testid="pricing-waitlist-count"
            >
              <Users size={12} />
              {waitlistCount.toLocaleString()}+ already reserving this deal
            </div>
          )}
        </div>

        {/* Feature comparison — Free vs PRO */}
        <div className="bg-white rounded-2xl shadow-soft mb-3 border border-[#E5E7EB] animate-fadeIn overflow-hidden" data-testid="pricing-features">
          {/* Column headers */}
          <div className="grid grid-cols-[minmax(0,1fr)_82px_82px] items-center border-b border-[#F3F4F6]">
            <div className="px-4 py-3 text-[11px] font-bold text-[#A0A7B4] uppercase tracking-wider">
              What's included
            </div>
            <div className="py-3 text-center bg-[#F7F9FB]" data-testid="pricing-free-col">
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#A0A7B4]">Free</div>
              <div className="text-[11px] font-semibold text-[#6B7280] mt-0.5">$0</div>
            </div>
            <div className="py-3 text-center bg-gradient-to-b from-[#4FB3A5]/10 to-[#4FB3A5]/5" data-testid="pricing-pro-col">
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#4FB3A5]">PRO</div>
              <div className="text-[11px] font-semibold text-[#1F3A5F] mt-0.5">{PRICING.yearly.price}/yr</div>
            </div>
          </div>

          {/* Comparison rows */}
          {COMPARISON.map((row, i) => (
            <div
              key={i}
              className={`grid grid-cols-[minmax(0,1fr)_82px_82px] items-center ${i < COMPARISON.length - 1 ? "border-b border-[#F3F4F6]" : ""}`}
              data-testid={`pricing-row-${i}`}
            >
              <div className="px-4 py-3 text-[13px] text-[#2C2C2C] font-medium leading-tight">
                {row.label}
              </div>
              <div className="py-3 text-center">
                {row.free === true ? (
                  <Check size={18} className="text-[#4FB3A5] mx-auto" strokeWidth={2.5} />
                ) : row.free === false ? (
                  <Minus size={16} className="text-[#D1D5DB] mx-auto" strokeWidth={2.5} />
                ) : (
                  <span className="text-[11px] text-[#6B7280] font-medium">{row.free}</span>
                )}
              </div>
              <div className="py-3 text-center bg-gradient-to-b from-[#4FB3A5]/5 to-transparent">
                {row.pro === true ? (
                  <Check size={18} className="text-[#4FB3A5] mx-auto" strokeWidth={2.5} />
                ) : row.pro === false ? (
                  <Minus size={16} className="text-[#D1D5DB] mx-auto" strokeWidth={2.5} />
                ) : (
                  <span className="text-[11px] text-[#1F3A5F] font-semibold">{row.pro}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Coming Soon to PRO */}
        <div
          className="bg-gradient-to-br from-white to-[#F0F9F7] rounded-2xl p-5 mb-6 border border-[#4FB3A5]/30 shadow-soft"
          data-testid="pricing-coming-soon"
        >
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={14} className="text-[#4FB3A5]" />
            <span className="text-[10px] uppercase tracking-wider font-bold text-[#4FB3A5]">
              Coming soon to PRO
            </span>
          </div>
          <p className="text-[12px] text-[#6B7280] mb-3 leading-snug">
            Every feature on this list ships free for pre-launch reservers — no price bump, ever.
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5 text-[12px]">
            {[
              { icon: Lightbulb,          title: "Daily texting tips",         desc: "Bite-sized coaching you'll actually use" },
              { icon: ShieldAlert,        title: "Red-flag detector",          desc: "Spot gaslighting & breadcrumbing" },
              { icon: MessageSquareHeart, title: "Dating translator",          desc: "Decode mixed signals instantly" },
              { icon: AlertTriangle,      title: "Real-time warning system",   desc: "Catch risky lines as you type" },
              { icon: Brain,              title: "Learns your style",          desc: "Rewrites that sound like you" },
              { icon: BookOpen,           title: "Boundary-script library",    desc: "Scripts for hard conversations" },
              { icon: Users,              title: "Full conversation fixes",    desc: "Paste the whole thread, get a plan" },
              { icon: TrendingUp,         title: "Smart weekly insights",      desc: "See where your texts go sideways" },
              { icon: Clock,              title: "Send-safe scheduler",        desc: "Delay + auto-recheck before send" },
              { icon: Heart,              title: "Partner & co-parent mode",   desc: "Share tone goals across accounts" },
              { icon: UserPlus,           title: "Group-chat rescue",          desc: "De-escalate the whole group, not just you" },
              { icon: PartyPopper,        title: "Texting Wrapped",            desc: "Your shareable year-in-review card" },
            ].map((f, i) => (
              <li key={i} className="flex items-start gap-2" data-testid={`coming-soon-${i}`}>
                <div className="w-6 h-6 rounded-lg bg-white border border-[#4FB3A5]/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <f.icon size={12} className="text-[#4FB3A5]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#1F3A5F] leading-tight">{f.title}</div>
                  <div className="text-[11px] text-[#6B7280] leading-snug">{f.desc}</div>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-4 bg-white rounded-xl border border-[#4FB3A5]/20 px-3 py-2.5 flex items-start gap-2">
            <Flame size={13} className="text-[#E07A7A] flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-[#1F3A5F] leading-snug">
              <span className="font-semibold">Pre-launch lock-in:</span> reserve today at $5.99/yr and
              you keep that rate for life — even after features above go live.
            </p>
          </div>
        </div>

        {/* Pricing Section */}
        <div className="space-y-3 mb-6" data-testid="pricing-section">
          {/* Yearly - Highlighted Offer */}
          <div
            className="relative rounded-2xl p-5 shadow-lg overflow-hidden cursor-pointer hover:scale-[1.01] active:scale-[0.99] transition-transform"
            onClick={handleYearly}
            style={{
              background: "linear-gradient(135deg, #1F3A5F 0%, #2a4a75 50%, #4FB3A5 100%)",
            }}
            data-testid="pricing-yearly-card"
          >
            {/* Best Value Badge */}
            <div className="absolute -top-0 right-5 bg-gradient-to-r from-[#FFD27F] to-[#FEB95A] text-[#8B4A00] text-[10px] font-bold px-3 py-1 rounded-b-lg shadow-md flex items-center gap-1">
              <Flame size={11} />
              BEST VALUE
            </div>

            <div className="text-white">
              <p className="text-[11px] uppercase tracking-wider font-bold text-white/80 mb-2">
                Limited Pre-Launch Offer
              </p>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-4xl font-bold" data-testid="yearly-price">{PRICING.yearly.price}</span>
                <span className="text-white/70 text-sm">for 12 months</span>
              </div>
              <p className="text-white/90 text-sm font-medium">
                That's {PRICING.yearly.perMonth}
              </p>

              {/* Comparison */}
              <div className="mt-3 flex items-center gap-2 text-xs text-white/70">
                <span className="line-through">{PRICING.monthly.price}×12 = $35.88</span>
                <span className="bg-white/20 text-white font-bold px-2 py-0.5 rounded-full">Save 83%</span>
              </div>

              <button
                className="w-full mt-4 bg-white text-[#1F3A5F] font-bold py-3.5 rounded-xl hover:bg-white/90 active:scale-95 transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-70"
                data-testid="yearly-cta-btn"
                onClick={(e) => { e.stopPropagation(); handleYearly(); }}
                disabled={purchasing !== null}
              >
                {purchasing === "yearly" ? (
                  <><Loader2 size={16} className="animate-spin" /> Processing…</>
                ) : IS_PRELAUNCH ? (
                  `Reserve $${PRICING.yearly.price.replace("$", "")}/year deal`
                ) : (
                  `Get 1 Year for ${PRICING.yearly.price}`
                )}
              </button>
              {IS_PRELAUNCH && (
                <p className="text-white/85 text-[11px] text-center mt-2 font-medium" data-testid="yearly-no-charge">
                  No charge today — activate when we launch on Google Play or the App Store
                </p>
              )}
            </div>
          </div>

          {/* Monthly - Secondary */}
          <div
            className="rounded-2xl p-4 bg-white border border-[#E5E7EB] hover:border-[#4FB3A5]/40 transition-colors cursor-pointer"
            onClick={handleMonthly}
            data-testid="pricing-monthly-card"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-[#A0A7B4] font-medium uppercase tracking-wider mb-1">Standard</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl font-bold text-[#1F3A5F]" data-testid="monthly-price">
                    {PRICING.monthly.price}
                  </span>
                  <span className="text-sm text-[#6B7280]">{PRICING.monthly.period}</span>
                </div>
              </div>
              <button
                className="text-sm font-medium text-[#2F5BFF] hover:underline"
                data-testid="monthly-cta-btn"
                onClick={(e) => { e.stopPropagation(); handleMonthly(); }}
              >
                {IS_PRELAUNCH ? "Remind me at launch →" : "Continue with monthly plan →"}
              </button>
            </div>
          </div>
        </div>

        {/* Trust Row */}
        <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 mb-4 text-xs text-[#6B7280]">
          <div className="flex items-center gap-1.5">
            <Check size={13} className="text-[#4FB3A5]" />
            Cancel anytime
          </div>
          <div className="flex items-center gap-1.5">
            <Shield size={13} className="text-[#4FB3A5]" />
            Private &amp; secure
          </div>
          <div className="flex items-center gap-1.5">
            <Sparkles size={13} className="text-[#4FB3A5]" />
            Founding member perks
          </div>
        </div>

        {/* Restore Purchases — required by Apple for any app with IAP */}
        {!IS_PRELAUNCH && (
          <div className="text-center mb-4">
            <button
              onClick={handleRestore}
              data-testid="restore-purchases-btn"
              className="text-xs text-[#6B7280] hover:text-[#1F3A5F] underline"
            >
              Restore previous purchase
            </button>
          </div>
        )}

        {/* Footer note */}
        <p className="text-center text-xs text-[#A0A7B4] px-4 leading-relaxed">
          {IS_PRELAUNCH
            ? "Pre-launch pricing. At launch, standard PRO is $2.99/month. Limited to early adopters."
            : "This pre-launch offer is available for a limited time. PRO pricing may change after launch."}
          <br />
          <a href="/terms" className="underline hover:text-[#1F3A5F]">Terms</a>
          {" · "}
          <a href="/privacy" className="underline hover:text-[#1F3A5F]">Privacy</a>
        </p>
      </div>

      <ReserveDealModal
        isOpen={reserveOpen}
        onClose={() => setReserveOpen(false)}
        source="pricing_page"
        defaultEmail={user?.email || ""}
      />
    </div>
  );
}
